/**
 * Hook per creare un checkout Lemon Squeezy via edge function.
 *
 * Chiama `supabase.functions.invoke('lemonsqueezy-create-checkout', { plan, billingCycle })`,
 * riceve l'URL del checkout LS e l'apre con `window.location.assign(url)`.
 *
 * L'edge function:
 *  - verifica il JWT dell'utente (verify_jwt=true)
 *  - controlla che l'utente sia owner/admin dell'org
 *  - chiama LS API POST /v1/checkouts con custom_data {organization_id, user_id}
 *  - ritorna { url, checkoutId }
 *
 * Status state: 'idle' | 'loading' | 'error'. Niente success — al success
 * facciamo direttamente il redirect.
 */
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { toast } from 'sonner';
import { Sentry } from '@/lib/sentry';

export type Plan = 'solo' | 'studio' | 'team';
export type BillingCycle = 'monthly' | 'yearly';

interface UseCreateCheckoutResult {
  createCheckout: (plan: Plan, billingCycle: BillingCycle) => Promise<void>;
  isCreatingCheckout: boolean;
  error: string | null;
}

export function useCreateCheckout(): UseCreateCheckoutResult {
  const { currentOrganizationId } = useCurrentOrganization();
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createCheckout = async (plan: Plan, billingCycle: BillingCycle): Promise<void> => {
    if (isCreatingCheckout) return;
    setError(null);
    setIsCreatingCheckout(true);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        'lemonsqueezy-create-checkout',
        {
          body: {
            plan,
            billingCycle,
            organizationId: currentOrganizationId,
          },
        },
      );

      if (invokeError) {
        throw new Error(invokeError.message || 'Errore chiamata edge function');
      }

      const url = (data as { url?: string })?.url;
      if (!url) {
        const errMsg = (data as { error?: string })?.error || 'Risposta inattesa dall\'API';
        throw new Error(errMsg);
      }

      // Redirect alla hosted checkout LS
      window.location.assign(url);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Errore sconosciuto';
      setError(msg);
      toast.error('Impossibile aprire il checkout', { description: msg });
      Sentry.captureException(e, {
        tags: { feature: 'lemonsqueezy-checkout' },
        extra: { plan, billingCycle, organizationId: currentOrganizationId },
      });
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  return { createCheckout, isCreatingCheckout, error };
}
